import axios from 'axios';
import api, { get, patch, post } from './https';

export type PartnerStatus = 'DRAFT' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';
export type PartnerType =
  | 'INSTALLER'
  | 'PLUMBER'
  | 'ELECTRICIAN'
  | 'ARCHITECT'
  | 'ESTATE_AGENT'
  | 'PROPERTY_DEVELOPER'
  | 'ENERGY_ASSESSOR'
  | 'MERCHANT'
  | string;

export type PartnerLeadStatus =
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'CONTACTED'
  | 'CONVERTED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'EXPIRED';

export type PartnerLeadProductType = 'air_conditioning' | 'heat_pump' | 'boiler' | 'other';
export type PartnerLeadJobType = 'service' | 'repair' | 'installation';

export interface ChannelPartnerSummary {
  id: string;
  fullName: string | null;
  phoneNumber: string;
  email: string | null;
  partnerType: PartnerType | null;
  status: PartnerStatus;
  derivedStatus?: PartnerStatus;
  onboardingStep: string;
  submitted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerStatsResponse {
  total: number;
  byStatus: Record<PartnerStatus, number>;
}

export interface PartnerListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  partnerType?: string;
}

export interface PartnerListResponse {
  status: boolean;
  data: ChannelPartnerSummary[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface IdentityQueueItem {
  checkId: string;
  partnerId: string;
  fullName: string;
  phoneNumber: string;
  partnerType: PartnerType | null;
  docId: string | null;
  docType: string | null;
  uploadedAt: string | null;
}

export interface VerificationCheckState {
  checkType: string;
  result: string;
  failureReason: string | null;
  reviewedBy: string | null;
  checkedAt: string | null;
}

export interface ChannelPartnerState {
  partner: {
    id: string;
    phoneNumber: string;
    email?: string;
    fullName?: string;
    dateOfBirth?: string;
    partnerType?: PartnerType;
    profilePhoto?: string;
    status: PartnerStatus;
    onboardingStep: string;
    submitted: boolean;
  };
  firm: null | {
    id: string;
    businessType: string;
    legalName: string;
    tradingName?: string;
    companiesHouseNumber?: string;
    vatNumber?: string;
  };
  address: null | {
    line1?: string;
    line2?: string;
    town?: string;
    county?: string;
    postcode?: string;
    uprn?: string;
  };
  bank: null | {
    accountHolderName: string;
    sortCode: string;
    copResult: string;
    copCheckedAt: string | null;
    isActive: boolean;
  };
  identityDocument: null | { id: string; docType: string; uploaded: boolean };
  checks: VerificationCheckState[];
  requiredChecks: string[];
  agreements: { agreementType: string; agreementVersion: string; acceptedAt: string }[];
  derivedStatus: PartnerStatus;
}

export interface PartnerLeadPartnerRef {
  _id: string;
  fullName?: string | null;
  phoneNumber?: string | null;
}

export interface PartnerLeadPhoto {
  storageKey: string;
  contentType?: string;
  sizeBytes?: number;
  uploadedAt?: string;
}

export interface PartnerLeadAddress {
  postcode: string;
  line1: string;
  line2?: string;
  town: string;
  county?: string;
}

export interface PartnerLeadEvent {
  _id: string;
  fromStatus?: PartnerLeadStatus;
  toStatus: PartnerLeadStatus;
  actor: 'partner' | 'admin' | 'system';
  actorId?: string;
  reason?: string;
  createdAt: string;
}

export interface PartnerLeadConversion {
  enquiry: string;
  enquiryNumber: string;
  // The Booking created alongside the Enquiry. /enquires lists bookings, so
  // this is what "Open enquiry" links to. Null for leads converted before
  // conversion started creating one.
  booking: string | null;
  bookingNumber: string;
  user: string | null;
  convertedAt: string | null;
}

// What ops has confirmed about "what job is this" while reviewing the lead
// (conversion panel's "Save" action), saved independently of actually
// converting. Mirrors marketingLeads.ts's MarketingLeadConversionDraft.
export interface PartnerLeadConversionDraft {
  subType?: string;
  serviceId?: string;
  quantity?: number;
  propertyType?: 'RESIDENTIAL' | 'COMMERCIAL';
  applianceKey?: string;
  applianceLabel?: string;
  componentKey?: string;
  componentLabel?: string;
  customAttributes?: PartnerConvertLeadCustomAttributes;
  updatedAt?: string;
  updatedBy?: string;
}

export interface PartnerLead {
  _id: string;
  channelPartnerLeadId: string;
  channelPartner?: PartnerLeadPartnerRef | null;
  productType: PartnerLeadProductType;
  jobType?: PartnerLeadJobType;
  equipmentType?: string;
  quantity: number;
  customerName: string;
  customerPhoneNumber: string;
  address: PartnerLeadAddress;
  notes?: string;
  photos: PartnerLeadPhoto[];
  status: PartnerLeadStatus;
  statusReason?: string;
  reviewedBy?: string;
  contactedAt?: string;
  createdAt: string;
  updatedAt: string;
  events?: PartnerLeadEvent[];
  conversion?: PartnerLeadConversion;
  conversionDraft?: PartnerLeadConversionDraft;
}

export interface PartnerLeadListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: PartnerLeadStatus | '';
  productType?: PartnerLeadProductType | '';
  channelPartner?: string;
}

export interface PartnerLeadListResponse {
  status: boolean;
  data: PartnerLead[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PartnerLeadStatsResponse {
  total: number;
  byStatus: Record<PartnerLeadStatus, number>;
}

export interface UpdatePartnerLeadStatusPayload {
  status: PartnerLeadStatus;
  statusReason?: string;
}

const BASE = '/api/v1/admin/partner-onboarding';
const LEAD_BASE = '/api/v1/admin/partner-lead';

const queryString = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

export const getChannelPartnerStats = async (): Promise<PartnerStatsResponse> => {
  const response = await get(`${BASE}/stats`);
  return response.data.data;
};

export const getChannelPartners = async (
  params: PartnerListParams,
): Promise<PartnerListResponse> => {
  const response = await get(
    `${BASE}/partners${queryString({
      page: params.page || 1,
      limit: params.limit || 10,
      search: params.search,
      status: params.status,
      partnerType: params.partnerType,
    })}`,
  );
  return response.data;
};

export const getChannelPartnerState = async (
  partnerId: string,
): Promise<ChannelPartnerState> => {
  const response = await get(`${BASE}/partners/${partnerId}`);
  return response.data.data;
};

export type BusinessType = 'LIMITED_COMPANY' | 'SOLE_TRADER';

export interface PartnerBusinessPayload {
  businessType: BusinessType;
  legalName: string;
  tradingName?: string;
  companiesHouseNumber?: string;
  utr?: string;
  vatNumber?: string;
}

export interface PartnerAddressPayload {
  postcode: string;
  line1: string;
  line2?: string;
  town: string;
  county?: string;
  uprn?: string;
}

export interface PartnerBankPayload {
  accountHolderName: string;
  sortCode: string;
  accountNumber: string;
}

export interface CreateChannelPartnerPayload {
  fullName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: string;
  partnerType?: string;
  profilePhoto?: string;
  business?: PartnerBusinessPayload;
  address?: PartnerAddressPayload;
  bank?: PartnerBankPayload;
}

export const createChannelPartner = async (
  payload: CreateChannelPartnerPayload,
) => {
  // ChannelPartner.countryCode is required on the backend schema, but the
  // admin "Add Partner" form has no country code input — omitting it made
  // every create request fail schema validation ("countryCode is required").
  // Partners created from admin are UK partners, so default it here.
  const response = await post(`${BASE}/partners`, {
    countryCode: '+44',
    ...payload,
  });
  if (response.status === 200 || response.status === 201) return response.data;
  throw new Error(response.data?.message || 'Failed to create partner');
};

// NOTE: unlike `post` (which resolves the full AxiosResponse), `patch` (see
// services/https.ts) already resolves to `response.data` — the parsed JSON
// body — and throws (with a message already extracted from the server's
// `message` field) on any non-2xx response. So there is no `.status`/`.data`
// wrapper to unwrap here; just return what `patch` resolves.
export const updatePartnerProfile = async (
  partnerId: string,
  payload: Partial<{
    fullName: string;
    email: string;
    dateOfBirth: string;
    partnerType: string;
    profilePhoto: string;
  }>,
) => patch(`${BASE}/partners/${partnerId}/profile`, payload);

export const updatePartnerBusiness = async (
  partnerId: string,
  payload: PartnerBusinessPayload,
) => patch(`${BASE}/partners/${partnerId}/business`, payload);

export const updatePartnerAddress = async (
  partnerId: string,
  payload: PartnerAddressPayload,
) => patch(`${BASE}/partners/${partnerId}/address`, payload);

export const updatePartnerBank = async (
  partnerId: string,
  payload: PartnerBankPayload,
) => patch(`${BASE}/partners/${partnerId}/bank`, payload);

export const suspendChannelPartner = async (partnerId: string) => {
  const response = await post(`${BASE}/partners/${partnerId}/suspend`);
  return response.data;
};

export const unsuspendChannelPartner = async (partnerId: string) => {
  const response = await post(`${BASE}/partners/${partnerId}/unsuspend`);
  return response.data;
};

export const getIdentityQueue = async (): Promise<IdentityQueueItem[]> => {
  const response = await get(`${BASE}/identity/queue`);
  return response.data.data || [];
};

export interface IdentityDocumentView {
  signedUrl: string;
  contentType?: string;
}

export const getIdentityDocumentViewUrl = async (
  docId: string,
): Promise<IdentityDocumentView> => {
  const response = await get(`${BASE}/identity/${docId}/view`);
  return response.data.data;
};

export const decideIdentityCheck = async (
  checkId: string,
  payload: { result: 'PASSED' | 'FAILED'; failureReason?: string },
) => {
  const response = await post(`${BASE}/identity/${checkId}/decision`, payload);
  return response.data;
};

export const getPartnerLeads = async (
  params: PartnerLeadListParams,
): Promise<PartnerLeadListResponse> => {
  const page = params.page || 1;
  const limit = params.limit || 10;
  const response = await get(
    `${LEAD_BASE}${queryString({
      page,
      limit,
      search: params.search,
      status: params.status,
      productType: params.productType,
      channelPartner: params.channelPartner,
    })}`,
  );
  const total = response.data.count || 0;
  return {
    status: response.data.status,
    data: response.data.data || [],
    pagination: {
      page,
      limit,
      total,
      totalPages: Math.max(1, Math.ceil(total / limit)),
    },
  };
};

export const getPartnerLeadStats = async (): Promise<PartnerLeadStatsResponse> => {
  const response = await get(`${LEAD_BASE}/stats`);
  return response.data.data;
};

export const getPartnerLead = async (leadId: string): Promise<PartnerLead> => {
  const response = await get(`${LEAD_BASE}/${leadId}`);
  return response.data.data;
};

export interface CreatePartnerLeadPayload {
  channelPartner: string;
  productType: PartnerLeadProductType;
  jobType?: PartnerLeadJobType;
  equipmentType?: string;
  quantity?: number;
  customerName: string;
  customerPhoneNumber: string;
  address: PartnerLeadAddress;
  notes?: string;
}

export const createPartnerLead = async (
  payload: CreatePartnerLeadPayload,
) => {
  const response = await post(`${LEAD_BASE}`, payload);
  if (response.status === 200 || response.status === 201) return response.data;
  throw new Error(response.data?.message || 'Failed to create lead');
};

export const updatePartnerLeadStatus = async (
  leadId: string,
  payload: UpdatePartnerLeadStatusPayload,
) => patch(`${LEAD_BASE}/${leadId}/status`, payload);

export const getPartnerLeadPhotoUrl = async (
  leadId: string,
  index: number,
): Promise<{ signedUrl: string; contentType?: string }> => {
  const response = await get(`${LEAD_BASE}/${leadId}/photos/${index}/view`);
  return response.data.data;
};

// ── Partner lead → Enquiry conversion ────────────────────────────────────
// Mirrors services/marketingLeads.ts's conversion contract — see
// nouvakserverapp/src/services/PartnerLeads/partnerLeadConversion.service.js
// for the server side. CONVERTED is unreachable through
// updatePartnerLeadStatus above (the server rejects it — see
// partnerLead.service.js#updateStatus); this is the only way to convert.

export type PartnerConversionWarningCode =
  | 'EXISTING_CUSTOMER'
  | 'OPEN_ENQUIRY_EXISTS'
  | 'DUPLICATE_LEAD';

export interface PartnerConversionWarning {
  code: PartnerConversionWarningCode;
  message: string;
}

export interface PartnerConversionExistingUser {
  id: string;
  name: string;
  phoneNumber: string;
  createdAt?: string;
  address?: PartialPartnerLeadAddress | null;
}

export interface PartnerConversionOpenEnquiry {
  id: string;
  enquiryNumber: string;
  subType: string;
  status: string;
  createdAt?: string;
}

export interface PartnerConversionOtherLead {
  id: string;
  channelPartnerLeadId: string;
  productType: PartnerLeadProductType;
  jobType: string;
  createdAt?: string;
  enquiryNumber: string;
}

export interface PartnerLeadConversionPreview {
  leadId: string;
  alreadyConverted: boolean;
  conversion: PartnerLeadConversion | null;
  convertible: boolean;
  status: PartnerLeadStatus;
  existingUser: PartnerConversionExistingUser | null;
  openEnquiries: PartnerConversionOpenEnquiry[];
  otherConvertedLeads: PartnerConversionOtherLead[];
  warnings: PartnerConversionWarning[];
}

export const getPartnerLeadConversionPreview = async (
  leadId: string,
): Promise<PartnerLeadConversionPreview> => {
  const response = await get(`${LEAD_BASE}/${leadId}/conversion-preview`);
  return response.data.data;
};

export interface PartialPartnerLeadAddress {
  postcode?: string;
  line1?: string;
  line2?: string;
  town?: string;
  county?: string;
}

// Step-4 intake-form answers, collected when the picked component triggers
// the form (componentKey 'other') or the job type isn't in the catalog.
export interface PartnerConvertLeadCustomAttributes {
  applianceOrPartType?: string;
  problemDescription?: string;
}

export interface ConvertPartnerLeadPayload {
  subType: string;
  serviceId?: string;
  quantity?: number;
  propertyType?: 'RESIDENTIAL' | 'COMMERCIAL';
  // Catalog picks (SbApplianceType/SbComponent) — see utils/dispatchableServices.ts.
  applianceKey?: string;
  applianceLabel?: string;
  componentKey?: string;
  componentLabel?: string;
  customAttributes?: PartnerConvertLeadCustomAttributes;
  customerName?: string;
  // Set when the admin manually attaches this lead to a specific existing
  // customer (search override) instead of the automatic phone-number match.
  existingUserId?: string;
  // Correction to the partner-submitted address, if the admin learns one
  // while contacting the customer. Any field left out falls back to
  // lead.address on the backend.
  address?: PartialPartnerLeadAddress;
  slot?: 'FIRST_HALF' | 'SECOND_HALF';
  date?: string;
  force?: boolean;
}

export interface ConvertPartnerLeadResult {
  lead: { id: string; status: PartnerLeadStatus };
  enquiry: {
    id: string;
    enquiryNumber: string;
    subType: string;
    status: string;
    service: { id: string; name: string } | null;
    applianceLabel?: string | null;
    componentLabel?: string | null;
  };
  // The dispatchable job ops works from at /enquires.
  booking: {
    id: string;
    bookingNumber: string;
    status: string;
    slot: string;
    date: string;
  };
  customer: {
    id: string;
    name: string;
    phoneNumber: string;
    reusedExisting: boolean;
  };
}

export interface PartnerConversionErrorData {
  requiresConfirmation?: boolean;
  warnings?: PartnerConversionWarning[];
  existingUser?: PartnerConversionExistingUser | null;
  openEnquiries?: PartnerConversionOpenEnquiry[];
  otherConvertedLeads?: PartnerConversionOtherLead[];
  conversion?: PartnerLeadConversion;
}

// Carries the structured 4xx payload the backend attaches to conversion
// errors — the plain-Error helpers above (get/post/patch in ./https) discard
// this, so convertPartnerLead talks to `api` directly and reconstructs it.
// Mirrors services/marketingLeads.ts's LeadConversionError.
export class PartnerLeadConversionError extends Error {
  statusCode: number;
  data?: PartnerConversionErrorData;

  constructor(statusCode: number, message: string, data?: PartnerConversionErrorData) {
    super(message);
    this.name = 'PartnerLeadConversionError';
    this.statusCode = statusCode;
    this.data = data;
  }
}

export const convertPartnerLead = async (
  leadId: string,
  payload: ConvertPartnerLeadPayload,
): Promise<ConvertPartnerLeadResult> => {
  try {
    const response = await api.post(`${LEAD_BASE}/${leadId}/convert`, payload);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const body = error.response.data as
        | { message?: string; data?: PartnerConversionErrorData }
        | undefined;
      throw new PartnerLeadConversionError(
        error.response.status,
        body?.message || 'Failed to convert this lead',
        body?.data,
      );
    }
    throw new PartnerLeadConversionError(0, 'No response from the server. Please try again.');
  }
};

// The conversion panel's "Save" action — persists an address correction
// and/or the category pick onto the lead without converting, so the work
// survives closing the panel. Mirrors marketingLeads.ts's updateLead.
export interface UpdatePartnerLeadPayload {
  address?: PartialPartnerLeadAddress;
  conversionDraft?: {
    subType?: string;
    serviceId?: string;
    quantity?: number;
    propertyType?: 'RESIDENTIAL' | 'COMMERCIAL' | '';
    applianceKey?: string;
    applianceLabel?: string;
    componentKey?: string;
    componentLabel?: string;
    customAttributes?: PartnerConvertLeadCustomAttributes;
  };
}

export const updatePartnerLead = async (
  leadId: string,
  payload: UpdatePartnerLeadPayload,
): Promise<PartnerLead> => {
  try {
    const response = await api.patch(`${LEAD_BASE}/${leadId}`, payload);
    return response.data.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const body = error.response.data as { message?: string } | undefined;
      throw new PartnerLeadConversionError(error.response.status, body?.message || 'Failed to save changes');
    }
    throw new PartnerLeadConversionError(0, 'No response from the server. Please try again.');
  }
};